import React from "react";
import cn from "classnames";
import styles from "./widget-library-panel.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { SubHeader } from "@src/components/atoms/sub-header/sub-header";
import { SubTitle } from "@src/components/atoms/sub-title/sub-title";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import { IconButton } from "@src/components/atoms/buttons/icon-button/icon-button";
import caretRight from "@src/assets/svg/caret-right.svg";
import widgetsIcon from "@src/assets/svg/widget-chart-line-blue.svg";
import typeTextIcon from "@src/assets/svg/widget-type-text-blue.svg";
import separatorIcon from "@src/assets/svg/widget-separator-blue.svg";
import linkIcon from "@src/assets/svg/link.svg";
import {
  updateEditWidgetDrawerModalTileId,
  updateModalsCloseDrawerModal,
} from "@src/reducers/your-reducer";
import { useI18N } from "@src/language/language-context";
import { RootState } from "@src/reducers/store";
import { WidgetTemplate, WidgetType } from "@src/types/dashboards";
import { getWidgetSize } from "@src/components/organisms/widgets/widget-sizes";
import { findFreeSlot } from "@src/components/organisms/widgets/find-free-slot";
import { addPendingWidget } from "@src/reducers/dashboards-slice";
import { useWidgetTemplates } from "@src/components/organisms/widget-library-panel/use-widget-templates";

interface WidgetLibraryPanelProps {
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

const cssClassName = "widget-library-panel";

// Static content tools (F7). Icons copied from the mock; names English for now.
// ponytail: visual-only until the BE exposes these widgets (DB or create API).
const STATIC_TOOLS: {
  key: string;
  name: string;
  description: string;
  icon?: string;
  text?: string;
}[] = [
  {
    key: "static-header",
    name: "Header Text",
    description: "Section title label",
    icon: typeTextIcon,
  },
  {
    key: "static-text",
    name: "Static Text",
    description: "Text annotation on the dashboard",
    text: "Aa",
  },
  {
    key: "static-separator",
    name: "Separator Line",
    description: "Visual grouping line",
    icon: separatorIcon,
  },
  {
    key: "static-link",
    name: "Link",
    description: "Link to another dashboard",
    icon: linkIcon,
  },
  // TODO: when static widget will be available, add it to the list of static tools. For now, we don't want to show it in the UI.
  /* {
    key: "static-image",
    name: "Image",
    description: "Embed a machine photo or company logo",
    icon: imageIcon,
  }, */
];

// ponytail: the static tools (header / text / separator) read their widget
// body AND `template_id` from the BE-injected widget-templates list (same
// hook the picker modal uses). Each `STATIC_TOOLS.key` matches a
// WidgetType, so we look up the template by `type` and stamp its real id
// onto the widget — no client-invented UUIDs in the POST payload.

export const WidgetLibraryPanel = ({
  isCollapsed,
  onToggleCollapse,
}: WidgetLibraryPanelProps) => {
  const dispatch = useDispatch();
  const i18n = useI18N();
  const selectedDashboard = useSelector((state: RootState) =>
    state.dashboards.dashboards.find(
      (d) => d.id === state.dashboards.selectedDashboardId,
    ),
  );

  // Same hook the picker modal uses — fetch the BE template catalog once
  // and map each static-tool key (== WidgetType) to its template row.
  // Match by `type` only — we trust the BE's `config_type` and stamp it
  // verbatim onto the widget so the POST body round-trips cleanly.
  const { templates } = useWidgetTemplates();
  const staticTemplatesByType: Partial<Record<WidgetType, WidgetTemplate>> = {};
  for (const t of templates) {
    if (
      t.type === "static-header" ||
      t.type === "static-text" ||
      t.type === "static-separator" ||
      t.type === "static-link"
    ) {
      staticTemplatesByType[t.type] = t;
    }
  }

  const title = i18n("pages.dashboardsPage.widgetLibrary");

  const openLibrary = () => {
    dispatch(
      updateModalsCloseDrawerModal({
        showModal: true,
        id: "widgetPickerModal",
      }),
    );
  };

  // Same wiring as `widget-picker-modal.handleAddWidget`: insert a pending
  // widget locally so the canvas shows it immediately, then open the edit
  // drawer on it. The actual commit (POST + PUT) happens in the drawer's
  // Save handler. The widget body and `template_id` come from the matching
  // `WidgetTemplate` row, so the POST sends a real backend catalog id, not
  // a client-invented placeholder.
  const handleStaticToolClick = (toolKey: string) => {
    const template = staticTemplatesByType[toolKey as WidgetType];
    if (!template || !selectedDashboard) return;
    const size = getWidgetSize(template.type);
    const columns = selectedDashboard.columnCount || 24;
    const occupied = Object.keys(selectedDashboard.widgets)
      .map((id) => selectedDashboard.positioning[id])
      .filter((p): p is NonNullable<typeof p> => !!p);
    const slot = findFreeSlot(occupied, columns, size.w, size.h);
    const positioning = { x: slot.x, y: slot.y, w: size.w, h: size.h };
    const tempId = `pending-${crypto.randomUUID()}`;
    dispatch(
      addPendingWidget({
        dashboardId: selectedDashboard.id,
        widget: {
          id: tempId,
          title: template.title,
          type: template.type,
          config_type: template.config_type,
          query: null,
          encoding: template.encoding,
          semantics: template.semantics,
          presentation: { ...template.presentation, transparent: true },
          template_id: template.id,
        },
        positioning,
      }),
    );
    dispatch(updateEditWidgetDrawerModalTileId(tempId));
    dispatch(
      updateModalsCloseDrawerModal({
        showModal: true,
        id: "editWidgetDrawerModal",
      }),
    );
  };

  if (isCollapsed) {
    // Same SubHeader as expanded (no separation line), caret flipped to point
    // left (expand); then icon+text WIDGETS / TOOLS sections (mock rail look).
    return (
      <div className={styles[cssClassName]}>
        <SubHeader variant={"less-padding"}>
          <FlexRowContainer justifyContent="center">
            <span
              className={cn(
                styles[`${cssClassName}__caret`],
                styles[`${cssClassName}__caret--flip`],
              )}
            >
              <IconButton fun={onToggleCollapse} icon={caretRight} />
            </span>
          </FlexRowContainer>
        </SubHeader>

        <div
          className={cn(styles[`${cssClassName}__rail-body`], {
            [styles[`${cssClassName}__rail-body--disabled`]]:
              !selectedDashboard,
          })}
        >
          <span className={styles[`${cssClassName}__rail-label`]}>
            {i18n("pages.dashboardsPage.widgetsSection")}
          </span>
          <button
            className={styles[`${cssClassName}__rail-icon`]}
            onClick={openLibrary}
            title={title}
          >
            <img src={widgetsIcon} alt="" />
          </button>

          <span className={styles[`${cssClassName}__rail-label`]}>
            {i18n("pages.dashboardsPage.toolsSection")}
          </span>
          {STATIC_TOOLS.map((tool) => (
            <button
              key={tool.key}
              className={styles[`${cssClassName}__rail-icon`]}
              onClick={() => handleStaticToolClick(tool.key)}
              title={tool.name}
            >
              {tool.icon ? (
                <img src={tool.icon} alt="" />
              ) : (
                <span className={styles[`${cssClassName}__rail-icon-text`]}>
                  {tool.text}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={styles[cssClassName]}>
      <SubHeader variant={"less-padding"}>
        <FlexRowContainer justifyContent="space-between">
          <span className={styles[`${cssClassName}__caret`]}>
            <IconButton fun={onToggleCollapse} icon={caretRight} />
          </span>
          <SubTitle text={title} />
          <span className={styles[`${cssClassName}__header-spacer`]} />
        </FlexRowContainer>
      </SubHeader>

      <div
        className={cn(styles[`${cssClassName}__body`], {
          [styles[`${cssClassName}__body--disabled`]]: !selectedDashboard,
        })}
      >
        <span className={styles[`${cssClassName}__section-label`]}>
          {i18n("pages.dashboardsPage.widgetsSection")}
        </span>

        <div
          className={cn(
            styles[`${cssClassName}__tool-row`],
            styles[`${cssClassName}__tool-row--clickable`],
          )}
          onClick={openLibrary}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              openLibrary();
            }
          }}
        >
          <div className={styles[`${cssClassName}__tool-icon`]}>
            <img src={widgetsIcon} alt="" />
          </div>
          <div className={styles[`${cssClassName}__tool-info`]}>
            <span className={styles[`${cssClassName}__tool-name`]}>
              {title}
            </span>
            <span className={styles[`${cssClassName}__tool-desc`]}>
              {i18n("pages.dashboardsPage.widgetLibraryHint")}
            </span>
          </div>
        </div>

        <span className={styles[`${cssClassName}__section-label`]}>
          {i18n("pages.dashboardsPage.toolsSection")}
        </span>

        <div className={styles[`${cssClassName}__tools`]}>
          {STATIC_TOOLS.map((tool) => (
            <div
              key={tool.key}
              className={cn(
                styles[`${cssClassName}__tool-row`],
                styles[`${cssClassName}__tool-row--clickable`],
              )}
              // Skip tools with no BE template yet (e.g. `static-image`
              // until its catalog row + editor ship) — clicking would add
              // a placeholder with no fields to edit.
              onClick={
                staticTemplatesByType[tool.key as WidgetType]
                  ? () => handleStaticToolClick(tool.key)
                  : undefined
              }
              role={
                staticTemplatesByType[tool.key as WidgetType]
                  ? "button"
                  : undefined
              }
              tabIndex={staticTemplatesByType[tool.key as WidgetType] ? 0 : -1}
              onKeyDown={
                staticTemplatesByType[tool.key as WidgetType]
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleStaticToolClick(tool.key);
                      }
                    }
                  : undefined
              }
            >
              <div className={styles[`${cssClassName}__tool-icon`]}>
                {tool.icon ? (
                  <img src={tool.icon} alt="" />
                ) : (
                  <span className={styles[`${cssClassName}__rail-icon-text`]}>
                    {tool.text}
                  </span>
                )}
              </div>
              <div className={styles[`${cssClassName}__tool-info`]}>
                <span className={styles[`${cssClassName}__tool-name`]}>
                  {tool.name}
                </span>
                <span className={styles[`${cssClassName}__tool-desc`]}>
                  {tool.description}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
